import React, {useEffect, useMemo, useState} from 'react'
import {getPripremePrometa} from '../../../api/paneliPromet'
import {PripremaPrometaModel} from '../../../models/PripremaPrometaModel'
import {useLocation, useNavigate} from 'react-router-dom'

/**
 * Heuristics for "close to coil sheet":
 * - If you have a field like item.Tip / item.Naziv / item.ArtNaziv, update `getCoilSheetHint()` to use it.
 * - For now it tries to match common keywords in any available string-ish field.
 */
const getCoilSheetHint = (item: any) => {
    const candidates: string[] = [
        item?.Naziv,
        item?.ArtNaziv,
        item?.artikal?.ArtNaziv,
        item?.RepromaterijalNaziv,
        item?.Opis,
        item?.Napomena,
        item?.Kvalitet,
        item?.Boja,
    ]
        .filter(Boolean)
        .map((x) => String(x))

    const haystack = candidates.join(' ').toLowerCase()
    const keywords = ['coil', 'coils', 'lim', 'sheet', 'tab', 'tabla', 'rolna', 'coil sheet', 'lim u rolni']
    return keywords.some((k) => haystack.includes(k))
}

const PrometRepromaterijalaPaneli: React.FC = () => {
    const [data, setData] = useState<PripremaPrometaModel[]>([])
    const [loading, setLoading] = useState(true)
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        if (location.state?.refresh) {
            navigate(location.pathname, {replace: true, state: {}})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state])

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await getPripremePrometa()
                if (Array.isArray(response.data)) {
                    setData(response.data)
                } else {
                    console.error('Unexpected response format:', response)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [])

    const grouped = useMemo(() => {
        const trake = data.filter((x: any) => Boolean(x.TrakaId))
        const rolne = data.filter((x: any) => !x.TrakaId)
        return {trake, rolne}
    }, [data])

    if (loading) {
        return (
            <div className='d-flex align-items-center justify-content-center py-10'>
                <span className='spinner-border me-3' role='status' aria-hidden='true' />
                <span className='text-muted fw-semibold'>Učitavanje...</span>
            </div>
        )
    }

    if (!data.length) {
        return (
            <div className='alert alert-light border d-flex align-items-center gap-3'>
                <i className='bi bi-inbox fs-3 text-muted' />
                <div>
                    <div className='fw-bold'>Nema priprema</div>
                    <div className='text-muted'>Trenutno nema stavki za promet.</div>
                </div>
            </div>
        )
    }

    const Card = ({item}: {item: any}) => {
        const isTraka = Boolean(item.TrakaId)
        const idLabel = isTraka ? `Traka: ${item.TrakaId}` : `Rolna: ${item.RolnaId}`
        const href = isTraka ? `promet/traka/${item.TrakaId}` : `promet/rolna/${item.RolnaId}`

        const isCoilSheetClose = getCoilSheetHint(item)

        // Darker / non-pastel identity (no bg-light-* on the whole card)
        const accentText = isTraka ? 'text-danger' : 'text-primary'
        const accentBorder = isTraka ? 'border-danger' : 'border-primary'
        const badgeClass = isTraka ? 'badge-light-danger' : 'badge-light-primary'
        const typeLabel = isTraka ? 'TRAKA' : 'ROLNA'
        const iconClass = isTraka ? 'bi-scissors' : 'bi-arrow-down-square'

        return (
            <div className='col-xl-4 col-md-6 mt-3' key={item.Id}>
                <a href={href} className='text-decoration-none'>
                    <div
                        className={clsx(
                            'card card-xl-stretch border hoverable',
                            'shadow-sm',
                            'bg-white',
                            'promet-card'
                        )}
                        style={{
                            borderLeftWidth: 4,
                            borderLeftStyle: 'solid',
                        }}
                    >
                        <div className={clsx('card-body p-5 border-start', accentBorder)} style={{borderLeftWidth: 4}}>
                            {/* Top row */}
                            <div className='d-flex align-items-start justify-content-between gap-3'>
                                <div className='d-flex align-items-center gap-3'>
                                    {/* Icon bubble */}
                                    <div
                                        className={clsx(
                                            'd-flex align-items-center justify-content-center rounded-3',
                                            'bg-light',
                                            'border',
                                            accentBorder
                                        )}
                                        style={{width: 46, height: 46}}
                                    >
                                        <i className={clsx('bi fs-2', iconClass, accentText)} />
                                    </div>

                                    <div>
                                        <div className={clsx('fw-bold fs-4 mb-1', accentText)}>{idLabel}</div>
                                        <div className='text-muted fw-semibold'>
                                            <i className='bi bi-person-circle me-2' />
                                            {item?.uposlenik?.UposlenikImePrezime ?? '—'}
                                        </div>
                                    </div>
                                </div>

                                <div className='d-flex flex-column align-items-end gap-2'>
                                    <span className={clsx('badge fw-bold', badgeClass)}>{typeLabel}</span>

                                    {isCoilSheetClose && (
                                        <span className='badge badge-light-warning fw-bold'>
                      <i className='bi bi-exclamation-triangle-fill me-2' />
                      Coil / Sheet
                    </span>
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            <div className='separator separator-dashed my-4'></div>

                            {/* Bottom row */}
                            <div className='d-flex align-items-center justify-content-between'>
                                <div className='text-muted d-flex align-items-center gap-2'>
                                    <i className='bi bi-clock' />
                                    <span>Spremno za promet</span>
                                </div>

                                <span className={clsx('btn btn-sm btn-icon', isTraka ? 'btn-light-danger' : 'btn-light-primary')}>
                  <i className='bi bi-arrow-right' />
                </span>
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        )
    }

    return (
        <>
            {/* Header */}
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4'>
                <div>
                    <div className='fw-bold fs-3'>Pripreme prometa</div>
                    <div className='text-muted'>Odaberi stavku za ulaz/izlaz repromaterijala</div>
                </div>

                <div className='d-flex align-items-center gap-2 flex-wrap'>
          <span className='badge badge-light-primary fw-bold'>
            <i className='bi bi-arrow-repeat me-2' />
            Rolne: {grouped.rolne.length}
          </span>
                    <span className='badge badge-light-danger fw-bold'>
            <i className='bi bi-scissors me-2' />
            Trake: {grouped.trake.length}
          </span>
                    <span className='badge badge-light fw-bold'>Ukupno: {data.length}</span>
                </div>
            </div>

            {/* Grid */}
            <div className='row'>
                {data.map((item: any) => (
                    <Card key={item.Id} item={item} />
                ))}
            </div>

            {/* Small local styling (no light backgrounds, better hover) */}
            <style>
                {`
          .promet-card {
            transition: transform .15s ease, box-shadow .15s ease;
          }
          .promet-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0,0,0,.08) !important;
          }
        `}
            </style>
        </>
    )
}

export default PrometRepromaterijalaPaneli

// local helper to avoid importing clsx if you don't already have it in this file
function clsx(...args: any[]) {
    return args.filter(Boolean).join(' ')
}
